/**
 * Sweep visit scheduling for Report Site browse: optional `sweepScheduledDate` (YYYY-MM-DD, local calendar).
 * Unset date = available any day until checked off.
 */

export function formatLocalDateYmd(d: Date = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const YMD = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Returns normalized YYYY-MM-DD or undefined if invalid / empty. */
export function normalizeSweepScheduledDateInput(raw: string): string | undefined {
  const s = String(raw ?? '').trim();
  if (!s) return undefined;
  const m = YMD.exec(s);
  if (!m) return undefined;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const dt = new Date(y, mo - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return undefined;
  return `${m[1]}-${m[2]}-${m[3]}`;
}

export type SweepBrowseFields = {
  sweepCheckedOff?: boolean;
  sweepScheduledDate?: string | null;
};

/** Same rule as Report Site → Browse sweep locations (plus optional visit date). */
export function sweepIsVisibleInReportSiteBrowse(loc: SweepBrowseFields, todayYmd: string): boolean {
  if (loc.sweepCheckedOff) return false;
  const sched = String(loc.sweepScheduledDate ?? '').trim();
  if (!sched) return true;
  return sched === todayYmd;
}

export function filterSweepLocationsForReportSiteBrowse<T extends SweepBrowseFields>(
  rows: T[],
  todayYmd: string = formatLocalDateYmd()
): T[] {
  return rows.filter((r) => sweepIsVisibleInReportSiteBrowse(r, todayYmd));
}

/** Short label for UI chips (uses runtime locale). */
export function formatSweepScheduledDateLabel(ymd: string): string {
  const m = YMD.exec(String(ymd).trim());
  if (!m) return String(ymd).trim();
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}
